import {
  addLiveChatEventMessage,
  resolveConversation,
  sendUserReply,
} from 'actions/live-chat';
import { closeWidget } from 'actions/main';
import {
  clearInMemoryData,
  clearWebStorage,
} from 'components/salesforce-messaging-one-chat/dataProvider';
import { closeEventSource } from 'components/salesforce-messaging-one-chat/eventSourceService';
import { closeConversation } from 'components/salesforce-messaging-one-chat/messagingService';
import { applyKustomerCoreFunctionWithCallback } from 'embed/kustomerCore';
import { applyZopimFunction } from 'embed/zopim';
import { useAppDispatch, useAppSelector } from 'hooks';
import { clearConversation } from 'persistence/useClearConversationOnTimeout';
import { clearSmoochSession } from 'persistence/utils';
import { useCallback } from 'react';
import { selectWidgetInitMethod } from 'reducers/config';
import {
  selectLiveChatIntegration,
  selectChatId,
  selectIntegrationConfig,
  selectLiveChatStatus,
  selectUserEndedChat,
  setUserEndedChat,
} from 'reducers/live-chat';
import {
  addScreenReaderNotification,
  selectConversationId,
  selectIsSolveConfigPreview,
} from 'reducers/workflows';
import { selectFeatureFlags } from 'reducers/workflows/index';
import Smooch from 'smooch';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { getErrorMessage } from 'utils/utils';
import { USER_LEFT_CHAT_MSG } from '../consts';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import { getSmoochLocalStorage } from './useHandleSuncoInitialization';

export const useHandleEndLiveChat = ({
  shouldResolveConversation = true,
  shouldSendUserLeftMessage = true,
}: {
  shouldResolveConversation?: boolean;
  /**
   * Used for Sunco only
   */
  shouldSendUserLeftMessage?: boolean;
} = {}) => {
  const dispatch = useAppDispatch();
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);
  const chatId = useAppSelector(selectChatId);
  const { integrationId } = useAppSelector(selectIntegrationConfig);
  const liveChatStatus = useAppSelector(selectLiveChatStatus);
  const isSolveConfigPreview = useAppSelector(selectIsSolveConfigPreview);
  const userEndedChat = useAppSelector(selectUserEndedChat);
  const conversationId = useAppSelector(selectConversationId);
  const widgetInitMethod = useAppSelector(selectWidgetInitMethod);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const isStayAfterEndLiveChatEnabled = featureFlags.includes(
    'stay_after_end_live_chat',
  );

  const handleEndLiveChat = useCallback(async () => {
    let shouldCloseWidget = true;

    switch (liveChatIntegration) {
      case 'sunco':
        if (shouldSendUserLeftMessage) {
          // Smooch methods can be undefined:
          try {
            await Smooch.sendMessage(USER_LEFT_CHAT_MSG, chatId);
          } catch {
            // Continue regardless of error
          }
        }

        // If inside agent chat and ff is enabled. We should stay in the current page
        // instead of clear everything and start a new convo.
        if (
          isStayAfterEndLiveChatEnabled &&
          liveChatStatus === 'active' &&
          !userEndedChat
        ) {
          // Need timeout to allow userLeftMessage request to be sent
          setTimeout(() => {
            dispatch(setUserEndedChat(true));
            dispatch(
              addLiveChatEventMessage({
                message: 'Chat ended',
              }),
            );
          }, 400);

          return;
        } else {
          // Need timeout to allow userLeftMessage request to be sent
          setTimeout(() => {
            // Smooch methods can be undefined:
            try {
              dispatch(
                sendTrackingEvent({
                  description:
                    'Sunco Live Chat cleared Smooch session -- useHandleEndLiveChat',
                  event_type: 'widget-smooch-event',
                  integration: 'sunco-live-chat',
                  ...getSmoochLocalStorage(integrationId),
                }),
              );
              clearSmoochSession(integrationId);
              Smooch.destroy();
            } catch {
              // Continue regardless of error
            }
            dispatch(clearConversation());
          }, 400);
        }

        break;
      case 'salesforce':
        closeConversation(chatId)
          .catch((err: unknown) => {
            sendErrorToSentry({
              additionalContext: { chatId },
              conversationId: conversationId,
              error: `Salesforce One Chat: Something went wrong in closing the conversation with conversation-id ${chatId}: ${getErrorMessage(err)}`,
            });
          })
          .finally(() => {
            closeEventSource().catch((err: unknown) => {
              sendErrorToSentry({
                additionalContext: { chatId },
                conversationId: conversationId,
                error: `Salesforce One Chat: Something went wrong in closing the Event Source (SSE): ${getErrorMessage(err)}`,
              });
            });

            clearWebStorage();
            clearInMemoryData();
            dispatch(clearConversation());
          });
        break;
      case 'zendesk':
        /**
         * when zendesk agent leaves it is set to inactive, sending another message
         * would start a new zendesk chat
         */
        if (liveChatStatus === 'inactive' || liveChatStatus === 'ended') {
          applyZopimFunction(widgetInitMethod, 'endChat', {
            clear_dept_id_on_chat_ended: true,
          });
          dispatch(clearConversation());
        } else {
          applyZopimFunction(widgetInitMethod, 'endChat', {
            clear_dept_id_on_chat_ended: true,
          });
          await dispatch(
            sendUserReply({
              addWidgetComponent: false,
              message: USER_LEFT_CHAT_MSG,
            }),
          );
          dispatch(clearConversation());
        }
        break;
      case 'kustomer':
        try {
          await applyKustomerCoreFunctionWithCallback('endConversation', {
            conversationId: chatId,
          });
        } catch (err) {
          sendErrorToSentry({
            additionalContext: { chatId },
            conversationId: conversationId,
            error: `Failed to end Kustomer One Chat conversation -- ${err}`,
          });
          dispatch(clearConversation());
          break;
        }

        if (userEndedChat || liveChatStatus === 'inactive') {
          /**
           * conversation was already ended by the user once meaning
           * we called endConversation or we already received the
           * onConversationEnd event so we don't need to wait
           * for the onSatifactionReceived event.
           */
          dispatch(clearConversation());
        } else {
          /**
           * conversation was ended in the above try block but we don't
           * want to close the widget to allow the onSatisfactionReceived
           * event to be triggered.
           */
          await dispatch(
            sendUserReply({
              addWidgetComponent: false,
              message: USER_LEFT_CHAT_MSG,
            }),
          );
          shouldCloseWidget = false;
          dispatch(setUserEndedChat(true));
        }
        break;
      case 'freshchat':
        if (shouldResolveConversation) {
          await dispatch(resolveConversation());
        }
        dispatch(clearConversation());
        break;

      default:
        await dispatch(sendUserReply({ message: USER_LEFT_CHAT_MSG }));
        dispatch(clearConversation());
        break;
    }

    if (shouldCloseWidget && !isSolveConfigPreview) {
      // close the widget after we end the live chat
      dispatch(closeWidget());
    }

    dispatch(addScreenReaderNotification('Chat ended'));
  }, [
    liveChatIntegration,
    isSolveConfigPreview,
    dispatch,
    shouldSendUserLeftMessage,
    liveChatStatus,
    userEndedChat,
    shouldResolveConversation,
    chatId,
    integrationId,
    widgetInitMethod,
    conversationId,
    isStayAfterEndLiveChatEnabled,
  ]);

  return handleEndLiveChat;
};
